// Feature-flag gating for the in-app User Guide (UserGuide.js).
//
// Two mechanisms, one flag vocabulary:
//  - Whole guides: GUIDE_FLAGS maps an index.json slug to the frontend flag name that must be on
//    for the guide to appear in the TOC and be reachable by URL.
//  - Sections: a guide may wrap a block in `<!-- feature: <FeaturesKey> -->` … `<!-- /feature -->`
//    (each marker on its own line). FEATURE_FLAGS maps the `Features:*` appsettings key used in
//    the marker to the frontend flag name. stripFeatureBlocks removes the whole block (markers
//    included) when the flag is off and only the marker lines when it is on.
//
// Both maps resolve to the same frontend flag names that FeatureFlagsContext's hooks read
// (`remediationWorkflows`, `showAiIntelligence`); the viewer evaluates each through its hook so the
// hook's default semantics (opt-in vs default-visible) are preserved. The PDF builder
// (tools/screenshots/build-pdf.mjs) strips only the marker lines: the PDF is always the full manual.

// `Features:<key>` (marker text) → frontend flag name (GET /config/features property).
export const FEATURE_FLAGS = {
  RemediationWorkflows: "remediationWorkflows",
  ShowAiIntelligence: "showAiIntelligence",
  Usoc: "usoc",
  Fusion: "fusion",
  RemediationScorecard: "remediationScorecard",
};

// index.json slug → frontend flag name.
export const GUIDE_FLAGS = {
  "22-remediation-workflows": FEATURE_FLAGS.RemediationWorkflows,
  "21-ai-intelligence": FEATURE_FLAGS.ShowAiIntelligence,
  "23-fusion": FEATURE_FLAGS.Fusion,
};

// Returns the frontend flag name gating a slug, or undefined when the guide is ungated.
export const guideFlagFor = (slug) => GUIDE_FLAGS[slug];

const OPEN_RE = /^[ \t]*<!--\s*feature:\s*([A-Za-z0-9_]+)\s*-->[ \t]*\r?$/;
const CLOSE_RE = /^[ \t]*<!--\s*\/feature\s*-->[ \t]*\r?$/;

// Strips feature blocks from guide markdown. `flags` is an object of frontend flag name → boolean
// (already resolved through the hooks). Rules:
//  - flag off → the block is removed, markers included;
//  - flag on → only the marker lines are removed;
//  - a marker naming a key not in FEATURE_FLAGS is treated as off (never show ungateable content);
//  - nesting is not supported: an opening marker inside a block is just a marker line;
//  - an opening marker with no closing marker strips (or unwraps) to the end of the file;
//  - a stray closing marker outside a block is dropped, so marker text never renders.
export const stripFeatureBlocks = (markdown, flags = {}) => {
  if (!markdown) return "";
  const out = [];
  let inBlock = false;
  let blockOn = false;
  for (const line of markdown.split("\n")) {
    if (inBlock) {
      if (CLOSE_RE.test(line)) {
        inBlock = false;
        continue;
      }
      if (blockOn && !OPEN_RE.test(line)) out.push(line);
      continue;
    }
    const open = OPEN_RE.exec(line);
    if (open) {
      const flagName = FEATURE_FLAGS[open[1]];
      inBlock = true;
      blockOn = Boolean(flagName && flags[flagName]);
      continue;
    }
    if (CLOSE_RE.test(line)) continue;
    out.push(line);
  }
  return out.join("\n");
};
